import React, { useEffect } from "react";
import { Printer, Home, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { REACT_APP_LOGO_URL } from "../../../constants";
import { getCourseDisplayName } from "../constants/courses";

interface Address {
  address?: string;
  district?: string;
  state?: string;
  pincode?: string | number;
}

interface DocumentEntry {
  url?: string;
  label?: string;
  key?: string;
}

interface SchoolDetails {
  board?: string;
  school?: string;
  state?: string;
  passingYear?: string | number;
  obtainedMarks?: string | number;
  totalMarks?: string | number;
  percentage?: string | number;
}

interface Payment {
  amount?: number;
  utr?: string;
  utrNumber?: string;
  date?: string;
  paymentDate?: string;
  status?: string;
}

interface ApplicationData {
  applicationId?: string;
  admissionId?: string;
  status?: string;
  createdAt?: string;
  submittedAt?: string;
  candidateName?: string;
  dob?: string;
  mobile?: string | number;
  email?: string;
  fathersName?: string;
  gender?: string;
  alternateMobile?: string | number;
  preferenceOrder?: string[] | string;
  physicalIdentification?: string;
  pwd?: boolean;
  correspondAddress?: Address;
  permanentAddress?: Address;
  neetRoll?: string;
  neetAIRank?: string | number;
  category?: string;
  neetCategoryRank?: string | number;
  intermediateSchool?: SchoolDetails;
  physics?: boolean;
  chemistry?: boolean;
  biology?: boolean;
  english?: boolean;
  fees?: number;
  payment?: Payment;
  documents?: Record<string, DocumentEntry>;
  formData?: Record<string, unknown>;
}

interface AlliedPrintApplicationProps {
  formData?: ApplicationData;
  onBack?: () => void;
  applicationId?: string;
}

const AlliedPrintApplication: React.FC<AlliedPrintApplicationProps> = ({
  formData = {},
  onBack,
  applicationId: propAppId,
}) => {
  const navigate = useNavigate();

  const appId = propAppId || formData.applicationId || "N/A";
  const status = formData.status || "Submitted";
  const submittedOn = formData.submittedAt || formData.createdAt;

  // Auto trigger print when loaded
  useEffect(() => {
    const timer = setTimeout(() => {
      window.print();
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  const rawDocs =
    formData.documents ||
    ((formData.formData as Record<string, unknown>)?.documents as Record<
      string,
      DocumentEntry
    >) ||
    {};

  const getPhotoUrl = (): string | null => {
    return rawDocs.photo?.url || rawDocs.passportPhoto?.url || null;
  };

  const getSigUrl = (): string | null => {
    return rawDocs.signature?.url || null;
  };

  const formatAddress = (addr?: Address): string => {
    if (!addr) return "N/A";
    const { address, district, state, pincode } = addr;
    if (!address && !district && !state && !pincode) return "N/A";
    return `${address || ""}${district ? `, ${district}` : ""}${
      state ? `, ${state}` : ""
    }${pincode ? ` - ${pincode}` : ""}`.trim();
  };

  const getCourseLabel = (value?: string[] | string): string => {
    return getCourseDisplayName(value);
  };

  const getGenderLabel = (val?: string): string => {
    if (!val) return "N/A";
    const map: Record<string, string> = {
      M: "Male",
      F: "Female",
      T: "Transgender",
      MALE: "Male",
      FEMALE: "Female",
      OTHER: "Transgender/Other",
    };
    return map[val.toUpperCase()] || val;
  };

  const getPassedSubjects = (): string => {
    const subjects = [];
    if (formData.physics) subjects.push("Physics");
    if (formData.chemistry) subjects.push("Chemistry");
    if (formData.biology) subjects.push("Biology");
    if (formData.english) subjects.push("English");
    return subjects.length > 0 ? subjects.join(", ") : "N/A";
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  const docConfig: Record<string, string> = {
    photo: "Passport Photograph",
    signature: "Candidate Signature",
    aadhar: "Aadhar Card",
    scoreCard: "NEET Score Card",
    marksheet12: "Class 12th Marksheet",
    categoryCert: "Category Certificate",
    pwdCert: "PwD Certificate",
    paymentCert: "Payment Proof / Receipt",
  };

  const getFieldLabelFromSchema = (fieldId: string): string | null => {
    const schema =
      (formData as any).schemaSnapshot ||
      (formData as any).applicationFormSchema ||
      ((formData.formData as any)?.schemaSnapshot);

    if (schema?.sections && Array.isArray(schema.sections)) {
      for (const section of schema.sections) {
        if (section.fields && Array.isArray(section.fields)) {
          for (const f of section.fields) {
            if (f.id === fieldId && f.label) {
              return f.label;
            }
          }
        }
      }
    }
    return null;
  };

  // Collect any document field IDs defined in schemaSnapshot
  const schemaDocKeys: string[] = [];
  const activeSchema =
    (formData as any).schemaSnapshot ||
    (formData as any).applicationFormSchema ||
    ((formData.formData as any)?.schemaSnapshot);

  if (activeSchema?.sections && Array.isArray(activeSchema.sections)) {
    for (const section of activeSchema.sections) {
      if (section.fields && Array.isArray(section.fields)) {
        for (const f of section.fields) {
          if (f.type === "file" || f.type === "image" || section.id === "documents") {
            schemaDocKeys.push(f.id);
          }
        }
      }
    }
  }

  const allDocKeys = Array.from(
    new Set([...Object.keys(docConfig), ...schemaDocKeys, ...Object.keys(rawDocs)])
  );

  const paymentObj = formData.payment;
  const paymentAmount = paymentObj?.amount ?? formData.fees;

  return (
    <div className="min-h-screen bg-white p-4 font-sans text-slate-900 printable-application print:min-h-0 print:p-0">
      {/* Controls Bar - Hidden during printing */}
      <div className="max-w-4xl mx-auto mb-6 flex justify-between items-center border-b pb-4 print:hidden">
        <button
          onClick={handleBack}
          className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-700 bg-white hover:bg-gray-100 rounded-lg text-xs font-semibold"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex gap-2">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-1 px-4 py-1.5 bg-blue-600 text-white hover:bg-blue-700 rounded-lg text-xs font-semibold"
          >
            <Printer className="w-4 h-4" /> Print Now
          </button>
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-700 bg-white hover:bg-gray-100 rounded-lg text-xs font-semibold"
          >
            <Home className="w-4 h-4" /> Go Home
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto border border-gray-300 p-6 space-y-6 print:border-0 print:p-0 print:space-y-4">
        {/* Header Block */}
        <div className="text-center border-b-2 border-slate-900 pb-4 relative pt-2 print:pb-3">
          {/* AIIMS Logo - Top Left Corner */}
          <div className="absolute top-0 left-0 w-24 h-24 flex items-center justify-center print:w-20 print:h-20">
            <img
              src={REACT_APP_LOGO_URL}
              alt="AIIMS Logo"
              className="max-w-full max-h-full object-contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.onerror = null;
                target.src = "https://via.placeholder.com/150?text=AIIMS";
              }}
            />
          </div>

          <div className="px-28 print:px-24">
            <h1 className="text-xl font-black uppercase tracking-tight text-slate-900 print:text-lg">
              ALL INDIA INSTITUTE OF MEDICAL SCIENCES, MANGALAGIRI
            </h1>
            <p className="text-sm font-bold text-slate-700 mt-1 uppercase print:text-xs">
              ALLIED HEALTH SCIENCES ADMISSION APPLICATION
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 text-left text-xs mt-4 pt-3 border-t print:mt-2 print:pt-2">
            <div>
              <p>
                Application ID: <strong>{appId}</strong>
              </p>
              <p>
                Status: <strong className="uppercase">{status}</strong>
              </p>
              {submittedOn && (
                <p>
                  Submitted On:{" "}
                  <strong>{new Date(submittedOn).toLocaleString("en-IN")}</strong>
                </p>
              )}
              <p>
                Print Date: <strong>{new Date().toLocaleDateString("en-IN")}</strong>
              </p>
            </div>
          </div>

          {/* Inline Photo rendering */}
          <div className="absolute top-0 right-0 w-28 h-32 border-2 border-slate-900 bg-slate-50 flex items-center justify-center overflow-hidden print:w-24 print:h-28">
            {getPhotoUrl() ? (
              <img
                src={getPhotoUrl()!}
                alt="Candidate"
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-[10px] text-gray-400 text-center font-bold">
                No Photo
              </span>
            )}
          </div>
        </div>

        {/* Dynamic Form Sections */}
        <div className="space-y-6 pt-4 print:space-y-4 print:pt-2">
          {/* Section 1: Personal Details */}
          <div className="space-y-3 print:space-y-2">
            <h2 className="text-sm font-extrabold uppercase bg-slate-100 text-slate-900 px-3 py-1.5 border-l-4 border-slate-900 print:break-after-avoid">
              Personal Details
            </h2>
            <table className="w-full text-xs border border-gray-300 border-collapse">
              <tbody>
                <tr className="border-b border-gray-300 print:break-inside-avoid">
                  <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                    Candidate Name
                  </td>
                  <td className="py-2 px-3 text-slate-900 print:py-1">
                    {formData.candidateName || "N/A"}
                  </td>
                </tr>
                <tr className="border-b border-gray-300 print:break-inside-avoid">
                  <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                    Father's Name
                  </td>
                  <td className="py-2 px-3 text-slate-900 print:py-1">
                    {formData.fathersName || "N/A"}
                  </td>
                </tr>
                <tr className="border-b border-gray-300 print:break-inside-avoid">
                  <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                    Date of Birth
                  </td>
                  <td className="py-2 px-3 text-slate-900 print:py-1">
                    {formData.dob
                      ? new Date(formData.dob).toLocaleDateString("en-IN")
                      : "N/A"}
                  </td>
                </tr>
                <tr className="border-b border-gray-300 print:break-inside-avoid">
                  <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                    Gender
                  </td>
                  <td className="py-2 px-3 text-slate-900 print:py-1">
                    {getGenderLabel(formData.gender)}
                  </td>
                </tr>
                <tr className="border-b border-gray-300 print:break-inside-avoid">
                  <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                    Mobile Number
                  </td>
                  <td className="py-2 px-3 text-slate-900 print:py-1">
                    {formData.mobile ? String(formData.mobile) : "N/A"}
                  </td>
                </tr>
                <tr className="border-b border-gray-300 print:break-inside-avoid">
                  <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                    Alternate Mobile
                  </td>
                  <td className="py-2 px-3 text-slate-900 print:py-1">
                    {formData.alternateMobile
                      ? String(formData.alternateMobile)
                      : "N/A"}
                  </td>
                </tr>
                <tr className="border-b border-gray-300 print:break-inside-avoid">
                  <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                    Email Address
                  </td>
                  <td className="py-2 px-3 text-slate-900 print:py-1">
                    {formData.email || "N/A"}
                  </td>
                </tr>
                <tr className="border-b border-gray-300 print:break-inside-avoid">
                  <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                    Category
                  </td>
                  <td className="py-2 px-3 text-slate-900 print:py-1">
                    {formData.category || "N/A"}
                  </td>
                </tr>
                <tr className="border-b border-gray-300 print:break-inside-avoid">
                  <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                    Person with Disability (PwD)
                  </td>
                  <td className="py-2 px-3 text-slate-900 print:py-1">
                    {formData.pwd ? "Yes" : "No"}
                  </td>
                </tr>
                <tr className="border-b border-gray-300 print:break-inside-avoid">
                  <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                    Physical Identification Mark
                  </td>
                  <td className="py-2 px-3 text-slate-900 print:py-1">
                    {formData.physicalIdentification || "N/A"}
                  </td>
                </tr>
                <tr className="border-b border-gray-300 print:break-inside-avoid">
                  <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                    Course Preference Order
                  </td>
                  <td className="py-2 px-3 text-slate-900 font-semibold break-words whitespace-normal leading-snug print:py-1">
                    {Array.isArray(formData.preferenceOrder) && formData.preferenceOrder.length > 0 ? (
                      <ol className="list-decimal list-inside space-y-1">
                        {formData.preferenceOrder.map((pref: string, idx: number) => (
                          <li key={idx} className="break-words">
                            {getCourseDisplayName(pref)}
                          </li>
                        ))}
                      </ol>
                    ) : (
                      getCourseLabel(formData.preferenceOrder)
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Section 2: Address Details */}
          <div className="space-y-3 print:space-y-2">
            <h2 className="text-sm font-extrabold uppercase bg-slate-100 text-slate-900 px-3 py-1.5 border-l-4 border-slate-900 print:break-after-avoid">
              Address Details
            </h2>
            <table className="w-full text-xs border border-gray-300 border-collapse">
              <tbody>
                <tr className="border-b border-gray-300 print:break-inside-avoid">
                  <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                    Correspondence Address
                  </td>
                  <td className="py-2 px-3 text-slate-900 print:py-1">
                    {formatAddress(formData.correspondAddress)}
                  </td>
                </tr>
                <tr className="border-b border-gray-300 print:break-inside-avoid">
                  <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                    Permanent Address
                  </td>
                  <td className="py-2 px-3 text-slate-900 print:py-1">
                    {formatAddress(formData.permanentAddress)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Section 3: NEET Details */}
          <div className="space-y-3 print:space-y-2">
            <h2 className="text-sm font-extrabold uppercase bg-slate-100 text-slate-900 px-3 py-1.5 border-l-4 border-slate-900 print:break-after-avoid">
              NEET Details
            </h2>
            <table className="w-full text-xs border border-gray-300 border-collapse">
              <tbody>
                <tr className="border-b border-gray-300 print:break-inside-avoid">
                  <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                    NEET Roll Number
                  </td>
                  <td className="py-2 px-3 text-slate-900 font-mono print:py-1">
                    {formData.neetRoll || "N/A"}
                  </td>
                </tr>
                <tr className="border-b border-gray-300 print:break-inside-avoid">
                  <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                    NEET All India Rank (AIR)
                  </td>
                  <td className="py-2 px-3 text-slate-900 print:py-1">
                    {formData.neetAIRank ? String(formData.neetAIRank) : "N/A"}
                  </td>
                </tr>
                <tr className="border-b border-gray-300 print:break-inside-avoid">
                  <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                    Category
                  </td>
                  <td className="py-2 px-3 text-slate-900 print:py-1">
                    {formData.category || "N/A"}
                  </td>
                </tr>
                <tr className="border-b border-gray-300 print:break-inside-avoid">
                  <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                    NEET Category Rank
                  </td>
                  <td className="py-2 px-3 text-slate-900 print:py-1">
                    {formData.neetCategoryRank
                      ? String(formData.neetCategoryRank)
                      : "N/A"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Section 4: Academic Details (12th) */}
          <div className="space-y-3 print:space-y-2">
            <h2 className="text-sm font-extrabold uppercase bg-slate-100 text-slate-900 px-3 py-1.5 border-l-4 border-slate-900 print:break-after-avoid">
              Academic Details (12th / Senior Secondary)
            </h2>
            <table className="w-full text-xs border border-gray-300 border-collapse">
              <tbody>
                <tr className="border-b border-gray-300 print:break-inside-avoid">
                  <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                    Board / University
                  </td>
                  <td className="py-2 px-3 text-slate-900 print:py-1">
                    {formData.intermediateSchool?.board || "N/A"}
                  </td>
                </tr>
                <tr className="border-b border-gray-300 print:break-inside-avoid">
                  <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                    School Name
                  </td>
                  <td className="py-2 px-3 text-slate-900 print:py-1">
                    {formData.intermediateSchool?.school || "N/A"}
                  </td>
                </tr>
                <tr className="border-b border-gray-300 print:break-inside-avoid">
                  <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                    School State
                  </td>
                  <td className="py-2 px-3 text-slate-900 print:py-1">
                    {formData.intermediateSchool?.state || "N/A"}
                  </td>
                </tr>
                <tr className="border-b border-gray-300 print:break-inside-avoid">
                  <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                    Year of Passing
                  </td>
                  <td className="py-2 px-3 text-slate-900 print:py-1">
                    {formData.intermediateSchool?.passingYear || "N/A"}
                  </td>
                </tr>
                <tr className="border-b border-gray-300 print:break-inside-avoid">
                  <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                    Marks Obtained / Total Marks
                  </td>
                  <td className="py-2 px-3 text-slate-900 print:py-1">
                    {formData.intermediateSchool?.obtainedMarks !== undefined &&
                    formData.intermediateSchool?.totalMarks !== undefined
                      ? `${formData.intermediateSchool.obtainedMarks} / ${formData.intermediateSchool.totalMarks}`
                      : "N/A"}
                  </td>
                </tr>
                <tr className="border-b border-gray-300 print:break-inside-avoid">
                  <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                    Percentage
                  </td>
                  <td className="py-2 px-3 text-slate-900 font-bold print:py-1">
                    {formData.intermediateSchool?.percentage !== undefined
                      ? `${formData.intermediateSchool.percentage}%`
                      : "N/A"}
                  </td>
                </tr>
                <tr className="border-b border-gray-300 print:break-inside-avoid">
                  <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                    Subjects Passed
                  </td>
                  <td className="py-2 px-3 text-slate-900 print:py-1">
                    {getPassedSubjects()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Section 5: Payment Information */}
          {(paymentObj || (paymentAmount !== undefined && paymentAmount > 0)) && (
            <div className="space-y-3 print:space-y-2">
              <h2 className="text-sm font-extrabold uppercase bg-slate-100 text-slate-900 px-3 py-1.5 border-l-4 border-slate-900 print:break-after-avoid">
                Payment Information
              </h2>
              <table className="w-full text-xs border border-gray-300 border-collapse">
                <tbody>
                  {paymentAmount !== undefined && (
                    <tr className="border-b border-gray-300 print:break-inside-avoid">
                      <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                        Amount
                      </td>
                      <td className="py-2 px-3 text-slate-900 font-bold print:py-1">
                        ₹{paymentAmount}
                      </td>
                    </tr>
                  )}
                  <tr className="border-b border-gray-300 print:break-inside-avoid">
                    <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                      UTR / Reference ID
                    </td>
                    <td className="py-2 px-3 text-slate-900 font-mono print:py-1">
                      {paymentObj?.utrNumber || paymentObj?.utr || "N/A"}
                    </td>
                  </tr>
                  <tr className="border-b border-gray-300 print:break-inside-avoid">
                    <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                      Payment Date
                    </td>
                    <td className="py-2 px-3 text-slate-900 print:py-1">
                      {paymentObj?.paymentDate || paymentObj?.date
                        ? new Date(
                            paymentObj.paymentDate || paymentObj.date!
                          ).toLocaleDateString("en-IN")
                        : "N/A"}
                    </td>
                  </tr>
                  <tr className="border-b border-gray-300 print:break-inside-avoid">
                    <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                      Payment Status
                    </td>
                    <td className="py-2 px-3 text-slate-900 font-bold uppercase print:py-1">
                      {paymentObj?.status || "N/A"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Section 6: Uploaded Documents Summary */}
          <div className="space-y-3 print:space-y-2">
            <h2 className="text-sm font-extrabold uppercase bg-slate-100 text-slate-900 px-3 py-1.5 border-l-4 border-slate-900 print:break-after-avoid">
              Uploaded Documents Summary
            </h2>
            <table className="w-full text-xs border border-gray-300 border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-gray-300 text-slate-700 print:break-inside-avoid">
                  <th className="py-1.5 px-3 border-r border-gray-300 text-left font-bold w-1/3 print:py-1">
                    Document Name
                  </th>
                  <th className="py-1.5 px-3 border-r border-gray-300 text-left font-bold print:py-1">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {allDocKeys.map((key) => {
                  const docEntry = rawDocs[key];
                  const isUploaded = !!docEntry?.url || !!docEntry?.key;
                  const label =
                    docEntry?.label ||
                    docConfig[key] ||
                    getFieldLabelFromSchema(key) ||
                    (key.startsWith("field_") || key.startsWith("Field_")
                      ? `Custom Document (${key})`
                      : key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()));

                  if (key === "categoryCert" && formData.category === "UR") return null;
                  if (key === "pwdCert" && !formData.pwd) return null;
                  if (!isUploaded && !docConfig[key] && !schemaDocKeys.includes(key)) return null;

                  return (
                    <tr key={key} className="border-b border-gray-300 print:break-inside-avoid">
                      <td className="py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                        {label}
                      </td>
                      <td className="py-2 px-3 text-slate-900 print:py-1">
                        {isUploaded ? (
                          <span className="text-green-700 font-semibold">
                            Uploaded
                          </span>
                        ) : (
                          <span className="text-gray-400 italic">Not Uploaded</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Candidate Signature & Declaration */}
          <div className="space-y-4 pt-4 print:space-y-3 print:pt-2 print:break-inside-avoid">
            <div className="p-4 bg-slate-50 border border-gray-300 rounded text-xs space-y-2 print:p-2.5 print:space-y-1">
              <span className="block font-bold text-slate-800 uppercase">
                Declaration Checklist Status: ACCEPTED
              </span>
              <p className="text-slate-600 leading-relaxed text-[10px]">
                I hereby declare that the above information is true, complete and
                correct. I understand that my candidature is liable to be rejected in the
                event of any misstatement detected before or after admission.
              </p>
              {submittedOn && (
                <p className="text-[10px] text-gray-500 font-bold">
                  Digitally submitted on:{" "}
                  {new Date(submittedOn).toLocaleString("en-IN")}
                </p>
              )}
            </div>

            <div className="flex justify-end items-end pt-6 print:pt-4">
              <div className="flex flex-col items-center">
                {getSigUrl() ? (
                  <div className="w-40 h-16 border border-gray-300 p-1 flex items-center justify-center overflow-hidden mb-2 print:w-36 print:h-14">
                    <img
                      src={getSigUrl()!}
                      alt="Candidate Signature"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="w-40 h-12 border border-dashed border-gray-300 flex items-center justify-center text-xs text-gray-400 mb-2 print:w-36 print:h-10">
                    No Signature Uploaded
                  </div>
                )}
                <span className="text-[10px] font-bold text-slate-700">
                  CANDIDATE SIGNATURE
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlliedPrintApplication;
